import { useCallback, useRef, useState } from 'react'
import { ApiClientError } from 'grabbitbot-legacy-ui'
import { describeError } from '../api/errorMessages'

/**
 * Shared plumbing for every MONEY-MOVING or ledger-writing action on this
 * back-office (Checkout session create, startonline, subscription start/
 * modify/cancel/restart, admin credit add/remove, mark product paid, month
 * close). Business-critical (legacy-migration RULES.md rule 3) — two
 * properties, both tested per page:
 *
 * 1. **Double-submit guard.** A synchronous in-flight ref per action key is
 *    the real guard (a second click in the same tick, before React has
 *    re-rendered the disabled button, is dropped here); the disabled button
 *    is only its visible half. Same pattern as secure.grabbitbot.com's
 *    `useAdminActions`.
 * 2. **Idempotency key per submit.** A fresh key for every NEW submit, and
 *    the SAME key again only when the user retries a submit whose outcome is
 *    unknown (network failure or 5xx — the charge may have happened) with
 *    identical parameters. A definitive answer (2xx or 4xx) retires the key.
 *    Whether the key is actually SENT is decided by the caller (see
 *    `SEND_IDEMPOTENCY_KEY` in src/config.ts) — the lifecycle is the same.
 */

export interface MoneyFeedback {
  tone: 'warning' | 'error' | 'success'
  text: string
}

export function describeMoneyError(err: unknown): MoneyFeedback {
  if (err instanceof ApiClientError) {
    if (err.status === 0 || err.status >= 500) {
      return {
        tone: 'error',
        text: 'Outcome unknown — the server did not answer cleanly. Check the transactions before trying again; a retry reuses the same request key.',
      }
    }
    if (err.status === 401) return { tone: 'error', text: 'Your session has expired — please log in again.' }
    if (err.status === 403) return { tone: 'error', text: 'You do not have permission for this action.' }
    if (err.status === 409 && err.code === 'request_in_progress') {
      return { tone: 'warning', text: 'The same request is still running — wait a moment, then check the result.' }
    }
    if (err.status === 422 && err.code === 'idempotency_key_reused') {
      return { tone: 'error', text: 'This request key was already used with different details — start a new submit.' }
    }
    if (err.status === 410) return { tone: 'warning', text: 'Not found (it may have been removed).' }
    return { tone: 'error', text: describeError(err) }
  }
  return { tone: 'error', text: describeError(err) }
}

function outcomeUnknown(err: unknown): boolean {
  return err instanceof ApiClientError ? err.status === 0 || err.status >= 500 : true
}

export function newIdempotencyKey(): string {
  return crypto.randomUUID()
}

interface PendingKey {
  fingerprint: string
  key: string
}

export function useMoneyAction() {
  const [busyKeys, setBusyKeys] = useState<ReadonlySet<string>>(new Set())
  const [feedback, setFeedback] = useState<Record<string, MoneyFeedback | undefined>>({})
  const inFlight = useRef<Set<string>>(new Set())
  const unknownOutcome = useRef<Map<string, PendingKey>>(new Map())

  /**
   * `fingerprint` identifies the parameters of this submit (e.g.
   * `JSON.stringify(body)`); a retry reuses the previous idempotency key
   * only when the fingerprint is identical AND the previous attempt's
   * outcome is unknown. Resolves to the action's result, or `undefined`
   * when the call was dropped (already in flight) or failed (feedback set).
   */
  const run = useCallback(
    async <T,>(
      actionKey: string,
      fingerprint: string,
      action: (idempotencyKey: string) => Promise<T>,
      onSuccess?: (result: T) => void,
    ): Promise<T | undefined> => {
      if (inFlight.current.has(actionKey)) return undefined
      inFlight.current.add(actionKey)
      setBusyKeys(new Set(inFlight.current))
      setFeedback((prev) => ({ ...prev, [actionKey]: undefined }))
      const previous = unknownOutcome.current.get(actionKey)
      const idemKey = previous && previous.fingerprint === fingerprint ? previous.key : newIdempotencyKey()
      try {
        const result = await action(idemKey)
        unknownOutcome.current.delete(actionKey)
        onSuccess?.(result)
        return result
      } catch (err) {
        if (outcomeUnknown(err)) unknownOutcome.current.set(actionKey, { fingerprint, key: idemKey })
        else unknownOutcome.current.delete(actionKey)
        setFeedback((prev) => ({ ...prev, [actionKey]: describeMoneyError(err) }))
        return undefined
      } finally {
        inFlight.current.delete(actionKey)
        setBusyKeys(new Set(inFlight.current))
      }
    },
    [],
  )

  const setFeedbackFor = useCallback((key: string, value: MoneyFeedback | undefined) => {
    setFeedback((prev) => ({ ...prev, [key]: value }))
  }, [])

  return {
    isBusy: (key: string) => busyKeys.has(key),
    feedbackFor: (key: string) => feedback[key],
    run,
    setFeedback: setFeedbackFor,
  }
}
